import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { CONTACT } from "@/i18n";
import { Menu, X, Phone, Globe, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Header() {
  const { t, lang, toggle, isRTL } = useLang();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/treatments", label: t("nav.treatments") },
    { to: "/hospitals", label: t("nav.hospitals") },
    { to: "/doctors", label: t("nav.doctors") },
    { to: "/services", label: t("nav.services") },
    { to: "/about", label: t("nav.about") },
    { to: "/blog", label: t("nav.blog") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header className="sticky top-0 z-40 bg-glass border-b border-stone-200/70" data-testid="site-header">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center gap-6 h-20">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
          <div className="h-11 w-11 rounded-2xl bg-sage-900 text-bone flex items-center justify-center font-display font-bold text-lg shadow-sm group-hover:rotate-3 transition-transform">
            AK
          </div>
          <div className="leading-tight whitespace-nowrap">
            <div className="font-display font-semibold text-ink text-base sm:text-lg">{lang === "ar" ? CONTACT.brand_ar : CONTACT.brand_en}</div>
            <div className="text-[10px] tracking-widest uppercase text-sage-900/70 font-medium hidden sm:block">{lang === "ar" ? "طب · سياحة · خدمة" : "Medical · Travel · Khidmat"}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ms-4">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-medium rounded-full transition-colors ${
                  isActive ? "text-sage-900 bg-sage-100" : "text-stone-700 hover:text-sage-900 hover:bg-stone-100"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="ms-auto flex items-center gap-2">
          <button
            onClick={toggle}
            data-testid="language-toggle-button"
            className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-full border border-stone-200 hover:border-sage-900 hover:text-sage-900 transition-colors"
          >
            <Globe className="h-4 w-4" />
            {lang === "en" ? "العربية" : "English"}
          </button>
          <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="hidden md:inline-flex" data-testid="header-whatsapp-link">
            <Button variant="ghost" className="rounded-full text-sage-900 hover:bg-sage-100">
              <MessageCircle className="h-4 w-4 me-2" /> {CONTACT.phone}
            </Button>
          </a>
          <Link to="/contact" data-testid="header-consult-btn">
            <Button className="rounded-full bg-ochre-500 hover:bg-ochre-600 text-white shadow-sm">
              {t("nav.consult")}
            </Button>
          </Link>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden ms-1 p-2 rounded-full border border-stone-200"
            aria-label="menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-stone-200 bg-bone" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-sm font-medium ${
                    isActive ? "bg-sage-100 text-sage-900" : "text-stone-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              onClick={() => { toggle(); setOpen(false); }}
              className="w-full text-start px-4 py-3 rounded-xl text-sm font-medium border border-stone-200 mt-2"
            >
              {lang === "en" ? "العربية" : "English"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
